import api from './api';
import { normalizeLanguage } from '../constants/Languages';
import { roundMoney } from '../utils/stockQuantityUtils';
import { readVatClassification, toVatPayload } from '../utils/vatClassification';

const DEFAULT_CURRENCY = 'EUR';

const withStoreId = (storeId, config = {}) => (storeId
  ? { ...config, _storeId: storeId }
  : config);

const normalizeSupportedLanguages = (supportedLanguages = []) => {
  const languages = [...(supportedLanguages || [])]
    .map((language) => normalizeLanguage(language))
    .filter((language, index, array) => array.indexOf(language) === index);

  return languages.length > 0 ? languages : ['en'];
};

const normalizeLocalizedDictionary = (value) => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (typeof entryValue !== 'string') {
      return accumulator;
    }

    accumulator[normalizeLanguage(key)] = entryValue;
    return accumulator;
  }, {});
};

const buildLocalizedPayload = (value) => (
  Object.fromEntries(
    Object.entries(normalizeLocalizedDictionary(value)).map(([language, entryValue]) => [
      language.toUpperCase(),
      entryValue.trim(),
    ]),
  )
);

const readOptionalNumber = (value) => {
  if (value == null || value === '') {
    return null;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toFormProduct = (product) => {
  const isRetail = product?.isRetail !== false;
  const salePrice = readOptionalNumber(product?.salePrice ?? product?.price);

  return {
    id: product?.id ?? null,
    name: normalizeLocalizedDictionary(product?.name),
    description: normalizeLocalizedDictionary(product?.description),
    price: isRetail && salePrice != null ? salePrice : '',
    salePrice: isRetail ? salePrice : null,
    isRetail,
    stockUnit: product?.stockUnit || 'Unit',
    contentQuantity: Number(product?.contentQuantity) || 1,
    lowStockThreshold: readOptionalNumber(product?.lowStockThreshold),
    stockOnHand: Number(product?.stockOnHand) || 0,
    lastUnitCost: roundMoney(readOptionalNumber(product?.lastUnitCost)),
    lastPurchaseDate: product?.lastPurchaseDate || null,
    hasStockMovements: Boolean(product?.hasStockMovements),
    disabled: Boolean(product?.disabled),
    imageUrl: product?.imageUrl || null,
    isOutOfStock: Boolean(product?.isOutOfStock),
    ...readVatClassification(product),
  };
};

const buildProductPayload = (product) => {
  const isRetail = product.isRetail !== false;
  const contentQuantity = Number(product.contentQuantity) || 1;
  const lowStockThreshold = product.lowStockThreshold == null || product.lowStockThreshold === ''
    ? null
    : Number(product.lowStockThreshold);

  return {
    name: buildLocalizedPayload(product.name),
    description: buildLocalizedPayload(product.description),
    salePrice: isRetail ? Number(product.salePrice ?? product.price) || 0 : null,
    isRetail,
    stockUnit: product.stockUnit || 'Unit',
    contentQuantity,
    lowStockThreshold: Number.isFinite(lowStockThreshold) ? lowStockThreshold : null,
    disabled: Boolean(product.disabled),
    ...toVatPayload(product),
  };
};

const ProductService = {
  toFormProduct,

  getProductManagementData: async (storeId) => {
    const response = await api.get('/products', withStoreId(storeId));
    const data = response.data;

    return {
      supportedLanguages: normalizeSupportedLanguages(data?.supportedLanguages),
      currency: data?.currency || DEFAULT_CURRENCY,
      productCommissionPercent: Number(data?.productCommissionPercent) || 0,
      products: Array.isArray(data?.products)
        ? data.products.map((product) => toFormProduct(product))
        : [],
    };
  },

  getProductCatalog: async (storeId, { activeOnly = true } = {}) => {
    const response = await api.get('/products/catalog', withStoreId(storeId, {
      params: { activeOnly },
    }));
    const items = Array.isArray(response.data) ? response.data : [];
    return items.map((product) => toFormProduct(product));
  },

  createProduct: async (storeId, product) => {
    const response = await api.post('/products', buildProductPayload(product), { _storeId: storeId });
    return toFormProduct(response.data);
  },

  updateProduct: async (storeId, productId, product) => {
    await api.put(`/products/${productId}`, buildProductPayload(product), { _storeId: storeId });
  },

  deleteProduct: async (storeId, productId) => {
    await api.delete(`/products/${productId}`, { _storeId: storeId });
  },

  setProductImage: async (storeId, productId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.patch(`/products/${productId}/image`, formData, withStoreId(storeId, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }));
    return response.data;
  },

  deleteProductImage: async (storeId, productId) => {
    await api.delete(`/products/${productId}/image`, { _storeId: storeId });
  },

  getProductSettings: async (storeId) => {
    const response = await api.get('/products/settings', withStoreId(storeId));
    return {
      productCommissionPercent: Number(response.data?.productCommissionPercent) || 0,
    };
  },

  updateProductSettings: async (storeId, settings) => {
    await api.put('/products/settings', {
      productCommissionPercent: Number(settings.productCommissionPercent) || 0,
    }, { _storeId: storeId });
  },
};

export default ProductService;
